import {
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { NotaFiscalGlobalListItem } from './types';

export interface NotaFiscalColaboradorExcluirDialogProps {
  open: boolean;
  nota: NotaFiscalGlobalListItem | null;
  loading: boolean;
  onConfirm: () => void;
  onClose: () => void;
}

function money(value: number | null | undefined): string {
  const n = Number(value);
  return (Number.isFinite(n) ? n : 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function Linha({ rotulo, valor }: { rotulo: string; valor: string }): JSX.Element {
  return (
    <Stack direction="row" spacing={2} justifyContent="space-between">
      <Typography variant="body2" color="text.secondary">
        {rotulo}
      </Typography>
      <Typography variant="body2">{valor}</Typography>
    </Stack>
  );
}

/** Conferência dos dados gravados antes de excluir a nota do colaborador. */
export function NotaFiscalColaboradorExcluirDialog({
  open,
  nota,
  loading,
  onConfirm,
  onClose,
}: NotaFiscalColaboradorExcluirDialogProps): JSX.Element {
  const { t } = useTranslation();
  const base = 'features.colaboradores.notasFiscaisGlobal.excluir';
  const mostrar = 'features.colaboradores.notasFiscaisGlobal.mostrar';
  // Flag da lista = checkNotaFiscalEmpresaNotaFiscalColaboradorEmitida.
  // Nota da empresa vinculada em outro status não bloqueia.
  const bloqueada = Boolean(nota?.existeNotaEmpresa);

  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t(`${base}.title`)}</DialogTitle>
      <DialogContent>
        {nota && (
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Typography variant="body2" color={bloqueada ? 'error' : 'text.secondary'}>
              {t(bloqueada ? `${base}.bloqueada` : `${base}.aviso`)}
            </Typography>
            <Linha rotulo={t(`${mostrar}.colaborador`)} valor={nota.colaboradorNome || '—'} />
            <Linha rotulo={t(`${mostrar}.cliente`)} valor={nota.clienteNome || '—'} />
            <Linha rotulo={t(`${mostrar}.competencia`)} valor={nota.periodo || '—'} />
            <Chip
              size="small"
              sx={{ alignSelf: 'flex-start' }}
              label={t(`features.colaboradores.notasFiscais.status.${nota.status}`)}
            />
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                {t(`${mostrar}.espelho`)}
              </Typography>
              <Stack spacing={0.75}>
                <Linha rotulo={t(`${mostrar}.espelhoHoras`)} valor={nota.espelhoTotalHoras || '—'} />
                <Linha rotulo={t(`${mostrar}.espelhoValor`)} valor={money(nota.espelhoValor)} />
              </Stack>
            </Paper>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                {t(`${mostrar}.apontamento`)}
              </Typography>
              <Stack spacing={0.75}>
                <Linha
                  rotulo={t(`${mostrar}.horasApontadas`)}
                  valor={nota.apontamentoTotalHorasApontadas || '—'}
                />
                <Linha rotulo={t(`${mostrar}.valorApontamento`)} valor={money(nota.apontamentoValor)} />
                <Linha rotulo={t(`${mostrar}.vale`)} valor={money(nota.valeValor)} />
                <Divider />
                <Linha rotulo={t(`${mostrar}.valorReceber`)} valor={money(nota.valorReceberFormula)} />
              </Stack>
            </Paper>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          {t('features.colaboradores.notasFiscaisGlobal.nova.cancelar')}
        </Button>
        <Button
          variant="contained"
          color="error"
          disabled={bloqueada || loading || !nota}
          onClick={onConfirm}
        >
          {t(`${base}.confirmar`)}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
